import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { presetForOpportunity } from '../data/oppPresets';
import './OpportunityRadar.css';

/**
 * 📡 Radar d'Opportunités — le CA qui dort, en images.
 * Donut des segments RFM + cartes chiffrées avec jauge de potentiel.
 * Doctrine : chaque info = texte + visuel simple et fun.
 */

const SEGMENTS_META = {
  champions: { label: 'Champions', emoji: '🏆', color: '#10B981' },
  fideles:   { label: 'Fidèles',   emoji: '💙', color: '#3B82F6' },
  nouveaux:  { label: 'Nouveaux',  emoji: '🌱', color: '#8B5CF6' },
  a_risque:  { label: 'À risque',  emoji: '⚠️', color: '#F59E0B' },
  endormis:  { label: 'Endormis',  emoji: '💤', color: '#94A3B8' },
};

export default function OpportunityRadar() {
  const navigate = useNavigate();
  const { tenant, user } = useApp();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // preview avant toute action

  // Doctrine : chaque carte atterrit sur une campagne DÉJÀ construite
  const goAction = (o) => {
    const company = tenant?.name || 'notre équipe';
    const owner = user?.name ? `${user.name.split(' ')[0]} — ${company}` : null;
    const built = presetForOpportunity(o, { company, owner });
    if (built) navigate('/campaigns/new', { state: built });
    else navigate(o.route);
  };

  useEffect(() => {
    api.getOpportunities().then(setData).catch(() => setError(true));
  }, []);

  if (error || (data && data.total_contacts === 0)) return null;

  if (!data) {
    return (
      <div className="oppr-card oppr-loading">
        <div className="oppr-loading-pulse">📡</div>
        <span>Analyse de ta base en cours…</span>
      </div>
    );
  }

  const donutData = Object.entries(data.segments)
    .filter(([, v]) => v > 0)
    .map(([key, value]) => ({ key, value, ...SEGMENTS_META[key] }));

  return (
    <div className="oppr-card">

      {/* ── Header ── */}
      <div className="oppr-head">
        <div className="oppr-head-left">
          <span className="oppr-head-icon">📡</span>
          <div>
            <h3 className="oppr-title">Radar d'opportunités</h3>
            <p className="oppr-sub">Le chiffre d'affaires qui dort dans ta base</p>
          </div>
        </div>
        {data.total_potential != null && data.total_potential > 0 && (
          <div className="oppr-total">
            <span className="oppr-total-val">~{data.total_potential.toLocaleString('fr-CH')} CHF</span>
            <span className="oppr-total-lbl">
              activables ce mois{data.basket_is_default ? ' *' : ''}
            </span>
          </div>
        )}
      </div>

      {/* ── Résumé base — ligne légère (donut mono-segment retiré) ── */}
      <div className="oppr-summary">
        <span className="oppr-summary-num">{data.total_contacts}</span>
        <span className="oppr-summary-txt">contacts dans ta base</span>
        {donutData.length > 1 && (
          <span className="oppr-summary-segs">
            {donutData.map(d => (
              <span key={d.key} className="oppr-summary-seg" title={d.label}>
                <span className="oppr-seg-dot" style={{ background: d.color }} />
                {d.emoji} <b>{d.value}</b> {d.label.toLowerCase()}
              </span>
            ))}
          </span>
        )}
      </div>

      {/* ── Opportunités : cartes pleine largeur ── */}
      <div className="oppr-opps">
        {data.opportunities.length === 0 ? (
          <div className="oppr-empty">
            🎉 Rien d'urgent cette semaine — ta base est bien travaillée !<br />
            <span>Envoie une campagne et le radar détectera de nouvelles opportunités.</span>
          </div>
        ) : data.opportunities.map((o, i) => (
          <div key={o.key} className={`oppr-opp oppr-heat-${o.heat}`} style={{ animationDelay: `${i * 70}ms` }}>
            <div className="oppr-opp-main">
              <span className="oppr-opp-icon">{o.icon}</span>
              <div className="oppr-opp-txt">
                <div className="oppr-opp-title">{o.title}</div>
                <div className="oppr-opp-desc">{o.desc}</div>
              </div>
            </div>
            <div className="oppr-opp-side">
              {o.potential != null && data.opportunities.length > 1 && (
                <span className="oppr-opp-chf">~{o.potential.toLocaleString('fr-CH')} CHF</span>
              )}
              {/* Carte automatique (vœux) : « Voir le programme » est une simple
                  navigation — pas de modale « préparation d'action » qui contredit
                  le « tu n'as rien à faire ». */}
              <button className="oppr-opp-cta" onClick={() => o.auto ? navigate(o.route) : setPendingAction(o)}>
                {o.cta} →
              </button>
            </div>
          </div>
        ))}
        {/* 🧲 Carte concours — comble le trou (impair) ou pleine largeur en bas (pair) */}
        <button type="button" className={`oppr-opp oppr-heat-cool oppr-acq${data.opportunities.length % 2 === 0 ? ' oppr-acq--wide' : ''}`} onClick={() => navigate('/collecte')}>
          <div className="oppr-opp-main">
            <span className="oppr-opp-icon">🧲</span>
            <div className="oppr-opp-txt">
              <div className="oppr-opp-title">Ta base vaut de l'or — fais-la grandir</div>
              <div className="oppr-opp-desc">Jeu-concours avec page de collecte : chaque participant = un contact consenti, anniversaire compris.</div>
            </div>
          </div>
          <div className="oppr-opp-side"><span className="oppr-opp-cta">Lancer une collecte →</span></div>
        </button>
      </div>

      {/* ── Modal preview : rien ne part sans validation ── */}
      {/* Portail vers document.body → échappe à overflow:hidden + transform du radar */}
      {pendingAction && createPortal(
        <div className="oppr-modal-backdrop" onClick={() => setPendingAction(null)}>
          <div className="oppr-modal" onClick={e => e.stopPropagation()}>
            <div className="oppr-modal-icon">{pendingAction.icon}</div>
            <h4 className="oppr-modal-title">{pendingAction.title}</h4>
            <p className="oppr-modal-desc">
              Tu vas être dirigé vers la préparation de cette action pour{' '}
              <strong>{pendingAction.count} contact{pendingAction.count > 1 ? 's' : ''}</strong>.
              <br />
              <strong>Rien ne sera envoyé</strong> sans ta validation finale — tu pourras
              relire le message et la cible avant tout envoi.
            </p>
            <div className="oppr-modal-actions">
              <button className="oppr-modal-cancel" onClick={() => setPendingAction(null)}>
                Annuler
              </button>
              <button
                className="oppr-modal-go"
                onClick={() => { const o = pendingAction; setPendingAction(null); goAction(o); }}
              >
                {pendingAction.cta} →
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}


      {data.no_chf ? null : (
      <p className="oppr-footnote">
        Estimations prudentes basées sur ton panier moyen
        {data.basket_is_default
          ? <> (* ~{data.basket} CHF, estimation pour ton métier{data.sector_label ? ` — ${data.sector_label}` : ''}) × taux sectoriels.{' '}
              <button type="button" className="oppr-footnote-link"
                onClick={() => navigate('/settings?tab=entreprise&focus=avg_client_value')}>
                Précise ton ticket moyen → chiffres exacts
              </button>
            </>
          : <> ({data.basket} CHF) × taux de conversion sectoriels.</>}
      </p>
      )}
    </div>
  );
}
